import Stack from '@mui/material/Stack'
import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import { observer } from 'mobx-react-lite'
import { useAuthStore } from '../AuthStore'
import onEnterProp from 'reusable/utils/on-enter-prop'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import TextFieldPassword from 'reusable/mui/text-field-password/TextFieldPassword'
import XeroAuthButton from 'features/xero-sso/XeroAuthButton'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import MuiLink from '@mui/material/Link'
import AuthSignInStore from './AuthSignInStore'
import useStore from 'reusable/mobx-store-utils'
import CenterFullScreen from 'reusable/mui/utilities/CenterFullScreen'
import AuthMessage from 'auth/shared/AuthMessage'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import { themeConfig } from '@gap/shared/theme'
import AuthVerification from 'auth/verification/AuthVerification'
import Box from '@mui/material/Box'
import { CheckBoxWithLabel } from 'reusable/mui/checkbox-with-label/CheckBoxWithLabel'
import TextFieldEmail from 'reusable/mui/text-field-email/TextFieldEmail'
import { logoutReasonMessages } from 'auth/logout-reason'

export default observer(function AuthSignIn() {
   const authStore = useAuthStore()
   const regionRouterStore = useRegionRouterStore()
   const authSignInStore = useStore(() => new AuthSignInStore({ authStore, regionRouterStore }))

   if (authStore.status === 'verify with mfa code') {
      return <AuthVerification
         method={authSignInStore.mfaMethod}
         {...authSignInStore.mfaMethod === 'email' ? {
            loginFormFields: authStore.fields
         } : {}}
         onVerify={authSignInStore.processSignInResponse}
      />
   }

   return <CenterFullScreen>
      <Stack sx={{ maxWidth: '564px', marginX: 'auto' }}>
         {authStore.displayPasswordResetSuccess && <AuthMessage message='Your password has been set. Please log in with your new password to continue.' />}
         {authStore.logoutReason && <AuthMessage message={logoutReasonMessages[authStore.logoutReason]} />}
         {authStore.displayEmailAddressUpdated && <AuthMessage message='Your email address has been updated. Please log in with your new email address to continue.' />}
      </Stack>
      <AuthCardWrapper title="Welcome back" subtitle="Log in to your account below">
         <form>
            <Stack sx={{ gap: 6 }}>
               <Stack sx={{ gap: 2 }}>
                  <XeroAuthButton onClick={authSignInStore.signInWithXero} />
                  <Divider>
                     <Typography variant='overline'>or</Typography>
                  </Divider>
                  <Stack id="signInEmail">
                     {authSignInStore.formHandler.email.bindInput(ip =>
                        <TextFieldEmail
                           autoFocus
                           fullWidth
                           label="Email*"
                           name="email"
                           autoComplete='email'
                           {...onEnterProp(authSignInStore.signIn)}
                           ip={ip}
                        />)}
                  </Stack>
                  <Stack id="signInPassword" sx={{ gap: .5, marginBottom: .5 }}>
                     {authSignInStore.formHandler.password.bindInput(ip =>
                        <TextFieldPassword
                           name="password"
                           fullWidth
                           label="Password*"
                           autoComplete='password'
                           {...onEnterProp(authSignInStore.signIn)}
                           {...ip}
                        />)}
                     <MuiLink component="button" type="button" variant="subtitle2" onClick={authSignInStore.navigateToForgotPassword} sx={{
                        alignSelf: 'end',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '0.8125rem', // 13px
                     }}>
                        Forgot password?
                     </MuiLink>
                  </Stack>

                  {authSignInStore.formHandler.rememberDevice.bindInput(ip =>
                     <CheckBoxWithLabel
                        label="Remember me for 30 days"
                        {...ip}
                     />)}

                  <ButtonWithProgress
                     loading={authSignInStore.loader.isLoading}
                     variant="contained"
                     size="large"
                     onClick={authSignInStore.signIn}>
                     Log in
                  </ButtonWithProgress>
               </Stack>
            </Stack>
            <Divider sx={{ mt: 3, mb: 1 }} />

            <Box display="flex" alignItems="center" sx={{ mb: -1 }}>
               <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                  Need your own advisor account?
               </Typography>
               <ButtonWithProgress
                  variant="text"
                  onClick={authSignInStore.goToTrialSignUp}
                  size="small"
                  sx={{ ml: 2 }}
               >
                  Sign up here
               </ButtonWithProgress>
            </Box>
         </form>
      </AuthCardWrapper>
   </CenterFullScreen>
})
